/**
 * BulkKhatabookImportPage — upload Khatabook "Book Customers" PDF, preview
 * matched + unmatched rows, bulk-set opening balances in 1 click.
 *
 * Semantics:
 *  - "You'll Get"  → PENDING  (humara paisa pending — they owe us)
 *  - "You'll Give" → ADVANCE  (admin k pas advance hai — we owe them)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Buildings, CreditCard, Gift, DeviceMobile, Lightning, ArrowClockwise,
  FilePdf, Warning, CheckCircle, XCircle, FloppyDisk, ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TABS = [
  { key: 'distributor', label: 'Distributors',         icon: Buildings    },
  { key: 'card',        label: 'Card Vendors',         icon: CreditCard   },
  { key: 'gift_card',   label: 'Gift Card Vendors',    icon: Gift         },
  { key: 'recharge',    label: 'Recharge Vendors',     icon: DeviceMobile },
  { key: 'electricity', label: 'Electricity Vendors',  icon: Lightning    },
];

export default function BulkKhatabookImportPage() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [entityType, setEntityType] = useState('distributor');
  const [parsed, setParsed] = useState(null);  // { as_of_date, rows, total_rows, ... }
  const [opening_date, setOpeningDate] = useState('');
  const [selected, setSelected] = useState({}); // {row_idx: true}
  const [parsing, setParsing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/data-reset-access/me');
        setMeta(r.data);
      } catch { setMeta({ can_access: false }); }
    })();
  }, []);

  // Auto-load stashed PDF (from single-upload page redirect when bulk format detected)
  useEffect(() => {
    if (!meta?.can_access) return;
    const raw = sessionStorage.getItem('pending_bulk_pdf');
    if (!raw) return;
    try {
      const obj = JSON.parse(raw);
      sessionStorage.removeItem('pending_bulk_pdf');
      if (obj?.entity_type) setEntityType(obj.entity_type);
      // Convert dataURL → File
      fetch(obj.data).then((r) => r.blob()).then((b) => {
        const f = new File([b], obj.name || 'khatabook.pdf', { type: obj.type || 'application/pdf' });
        onPickFile(f);
      });
    } catch (e) { console.warn('pending_bulk_pdf parse failed', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.can_access]);

  const onPickFile = async (file) => {
    if (!file) return;
    setParsing(true);
    setParsed(null);
    setSelected({});
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/data-reset/parse-khatabook-bulk?entity_type=${entityType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParsed(r.data);
      setOpeningDate(r.data.as_of_date || new Date().toISOString().slice(0, 10));
      // Auto-select all matched rows
      const sel = {};
      (r.data.rows || []).forEach((row, i) => { if (row.matched_entity) sel[i] = true; });
      setSelected(sel);
      toast.success(`✅ ${r.data.matched_count}/${r.data.total_rows} matched`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Parse failed');
    } finally { setParsing(false); }
  };

  const toggleRow = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));
  const toggleAllMatched = () => {
    if (!parsed) return;
    const allMatched = parsed.rows.every((r, i) => !r.matched_entity || selected[i]);
    const sel = {};
    if (!allMatched) parsed.rows.forEach((r, i) => { if (r.matched_entity) sel[i] = true; });
    setSelected(sel);
  };

  const matchedRows = parsed?.rows?.filter((r) => r.matched_entity) || [];
  const unmatched = parsed?.rows?.filter((r) => !r.matched_entity) || [];
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedTotal = parsed?.rows
    ?.filter((_, i) => selected[i])
    .reduce((acc, r) => acc + (r.amount || 0), 0) || 0;

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== 'RESET') { toast.error('Type "RESET" to confirm'); return; }
    if (selectedCount === 0) { toast.error('Koi row select nahi'); return; }
    const items = (parsed.rows || [])
      .map((r, i) => ({ r, i }))
      .filter(({ i }) => selected[i])
      .map(({ r }) => ({
        entity_id: r.matched_entity.id,
        opening_amount: r.amount,
        kind: r.kind,
        note: `KhataBook bulk: ${r.customer_name}`,
      }));
    setBusy(true);
    try {
      const resp = await api.post('/data-reset/execute-bulk', {
        entity_type: entityType,
        opening_date: opening_date || undefined,
        items,
        confirm: 'RESET',
      });
      toast.success(`✅ ${resp.data.success_count} successful, ${resp.data.fail_count} failed`);
      if (resp.data.fail_count > 0) console.warn('Failures:', resp.data.failures);
      // Re-parse to show updated matched balances
      setConfirmText('');
      setSelected({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk execute failed');
    } finally { setBusy(false); }
  };

  if (!meta) return <div className="p-8 text-center text-sm text-gray-500">Loading...</div>;
  if (!meta.can_access) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
        <Warning size={32} weight="duotone" className="mx-auto text-red-500 mb-2" />
        <div className="font-semibold text-red-900">Access Denied</div>
        <p className="text-sm text-red-700 mt-1">Admin se Data Reset access enable karwao. (Role: {user?.role})</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="bulk-khatabook-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FilePdf size={26} weight="duotone" className="text-fuchsia-600" /> Bulk KhataBook Import
        </h1>
        <div className="ml-auto flex gap-2">
          <Link to="/admin/data-reset"><Button size="sm" variant="outline">← Data Reset (single)</Button></Link>
          <Link to="/admin/data-recovery"><Button size="sm" variant="outline" className="gap-1"><ArrowCounterClockwise size={14} /> Recovery</Button></Link>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
        💡 <b>KhataBook → Customers → "Share Customer List as PDF"</b> wala PDF yahan upload karo. System har row me <b>"You'll Get"</b> → PENDING (humara paisa) aur <b>"You'll Give"</b> → ADVANCE (humein dena) automatically detect karega. Matched rows ko bulk-import kar denge.
      </div>

      {/* Entity Type Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = entityType === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => { setEntityType(t.key); setParsed(null); }}
              data-testid={`bulk-tab-${t.key}`}
              className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-md border-b-2 transition flex items-center gap-1 ${
                isActive ? 'border-fuchsia-600 text-fuchsia-700 bg-fuchsia-50' : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Upload */}
      <div className="bg-white border rounded-xl p-4">
        <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onPickFile(f); }} />
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => fileRef.current?.click()} disabled={parsing} className="bg-fuchsia-600 hover:bg-fuchsia-700 gap-1" data-testid="bulk-upload-btn">
            <FilePdf size={14} /> {parsing ? 'Parsing PDF…' : 'Upload Khatabook Customer-List PDF'}
          </Button>
          {parsed && (
            <div className="text-xs text-gray-600">
              Parsed: <b>{parsed.total_rows}</b> rows · <span className="text-emerald-700">{parsed.matched_count} matched</span> · <span className="text-amber-700">{parsed.unmatched_count} unmatched</span>
              {parsed.as_of_date && <> · As of <b>{parsed.as_of_date}</b></>}
            </div>
          )}
        </div>
      </div>

      {parsed && (
        <>
          {/* Matched rows table */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center gap-2">
              <CheckCircle size={18} className="text-emerald-600" weight="duotone" />
              <span className="font-semibold">Matched Rows ({matchedRows.length})</span>
              <button onClick={toggleAllMatched} className="ml-2 text-xs text-fuchsia-600 hover:underline">
                Select All / None
              </button>
              <div className="ml-auto text-xs">
                Selected: <b>{selectedCount}</b> · Total: <b>{fmt(selectedTotal)}</b>
              </div>
            </div>
            {matchedRows.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No matched rows in this PDF for {entityType}.</div>
            ) : (
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left w-8"><input type="checkbox" checked={matchedRows.length > 0 && matchedRows.every((_, idx) => selected[parsed.rows.indexOf(matchedRows[idx])])} onChange={toggleAllMatched} /></th>
                      <th className="px-3 py-2 text-left">KhataBook Name</th>
                      <th className="px-3 py-2 text-left">Matched To</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r, i) => r.matched_entity && (
                      <tr key={i} className="border-t hover:bg-gray-50" data-testid={`bulk-row-${i}`}>
                        <td className="px-3 py-1.5">
                          <input type="checkbox" checked={!!selected[i]} onChange={() => toggleRow(i)} data-testid={`bulk-row-check-${i}`} />
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="font-medium">{r.customer_name}</div>
                          {r.mobile && <div className="text-[10px] text-gray-500 font-mono">{r.mobile}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-emerald-700 font-semibold">{r.matched_entity.name}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.kind === 'advance' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {r.kind === 'advance' ? 'ADVANCE' : 'PENDING'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-bold">{fmt(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Unmatched */}
          {unmatched.length > 0 && (
            <details className="bg-white border rounded-xl">
              <summary className="cursor-pointer px-4 py-3 font-semibold text-gray-700 flex items-center gap-2">
                <XCircle size={16} className="text-amber-600" weight="duotone" />
                Unmatched ({unmatched.length}) — System ne match nahi kar paya
              </summary>
              <div className="overflow-x-auto max-h-[40vh] border-t">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">KhataBook Name</th>
                      <th className="px-3 py-2 text-left">Mobile</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatched.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-1.5">{r.customer_name}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px]">{r.mobile || '—'}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${r.kind === 'advance' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {r.kind === 'advance' ? 'ADVANCE' : 'PENDING'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right">{fmt(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Submit panel */}
          <div className="bg-white border border-red-200 rounded-xl p-4 space-y-3 sticky bottom-2">
            <div className="text-sm font-semibold text-red-700 flex items-center gap-1">
              <Warning size={16} weight="bold" /> Bulk Apply — Selected {selectedCount} entries
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div>
                <label className="text-xs font-semibold text-gray-700">Opening Date (sab pe applied)</label>
                <Input type="date" value={opening_date} onChange={(e) => setOpeningDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} data-testid="bulk-opening-date" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-red-700">Type <b>RESET</b> to confirm</label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder='Type "RESET"' className={confirmText.trim().toUpperCase() === 'RESET' ? 'border-emerald-500' : 'border-red-300'} data-testid="bulk-confirm" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={submit}
                disabled={busy || selectedCount === 0 || confirmText.trim().toUpperCase() !== 'RESET'}
                className="bg-red-600 hover:bg-red-700 gap-1"
                data-testid="bulk-submit"
              >
                <FloppyDisk size={14} /> {busy ? 'Applying…' : `Apply ${selectedCount} Resets`}
              </Button>
            </div>
            <div className="text-[11px] text-gray-500">
              Har row apna recovery batch banata hai — galat ho jaye to <Link to="/admin/data-recovery" className="text-fuchsia-600 underline">Data Recovery</Link> page se individually restore kar sakte ho.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
